import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons"
import useWindowDimensions from "../../hooks/useWindowDimensions"
import StoryCard from "./StoryCard"
import "./StoryCardCarousel.css"

// True modulo operator. Used for story carousel cycling
// (JavaScript's % operator returns remainder)
const mod = (dividend, modulus) => ((dividend % modulus) + modulus) % modulus

/**
 * Display a sequence of story cards in a carousel that's responsive to screen width
 * @param {StoryCardContent[]} storyCards 
 * @returns {JSX}
 */
export const StoryCardCarousel = ({ storyCards }) => {
  const { width } = useWindowDimensions()

  // Responsive itemsToShow based on viewport width. If all cards won't fit on
  // screen at once, always show odd number to help centering
  // Dynamically calculate itemsToShow based on screen width and card width
  const CARD_WIDTH = (width > 480) ? 370 : 280 // px, matches .manual-carousel-item
  const GAP = (width > 480) ? 24 : 16 // px, matches gap in carousel-track
  let itemsToShow = Math.max(1, Math.floor(width / (CARD_WIDTH + GAP)));
  const showingAllStories =  storyCards.length <= itemsToShow
  if (showingAllStories) {
    itemsToShow = storyCards.length
  } else if (itemsToShow % 2 === 0) {
    itemsToShow -= 1
  }
  const [current, setCurrent] = React.useState(0) // index of card in center of screen
  React.useEffect(() => {
    // Whenever the number of storyCards changes (for example, when they first load or
    // during a search), reset current position
    setCurrent(0)
  }, [storyCards.length])

  const goPrev = () => setCurrent((c) => mod(c - 1, storyCards.length))
  const goNext = () => setCurrent((c) => mod(c + 1, storyCards.length))

  // Calculate the window of visible cards (always exactly itemsToShow)
  const storiesPerSide = Math.floor(itemsToShow / 2)
  const start = mod(current - storiesPerSide, storyCards.length)
  let visibleCardIndices = Array.from({length: itemsToShow}, (_, idx) => mod(start + idx, storyCards.length))
  let visibleCards = visibleCardIndices.map(i => storyCards[i])

  // Find the next and previous storyCards for faded preview
  const nextIdx = mod(start + itemsToShow, storyCards.length)
  const prevIdx = mod(start - 1, storyCards.length)
  const nextCard = showingAllStories ? {} : storyCards[nextIdx]
  const prevCard = showingAllStories ? {} : storyCards[prevIdx]

  return (
    <div className="manual-carousel-container" style={{ position: "relative" }}>
      <div
        className="manual-carousel-track"
        style={{ height: (width > 480) ? "460px" : "348px", gap: `${GAP}px` }}
      >
        {!showingAllStories && (
          <div
            className="manual-carousel-item carousel-item-prev-preview"
            style={{
              flex: `0 0 ${CARD_WIDTH}px`,
              minWidth: `${CARD_WIDTH}px`,
            }}
            onClick={() => setCurrent(prevIdx)}
            inert="true"
          >
            <StoryCard {...prevCard} isPreview={true} />
          </div>
        )}
        {visibleCards.map((card, idx) => (
          <div
            key={card.slug || start + idx}
            className="manual-carousel-item"
            style={{
              flex: `0 0 ${CARD_WIDTH}px`,
              minWidth: `${CARD_WIDTH}px`,
            }}
          >
            <StoryCard
              {...card}
              isFirst={!showingAllStories && (idx === 0)}
              isLast={!showingAllStories && (idx === visibleCards.length - 1)}
              prevCard={prevCard}
              nextCard={nextCard}
            />
          </div>
        ))}
        {!showingAllStories && (
          <div
            className="manual-carousel-item carousel-item-next-preview"
            style={{
              flex: `0 0 ${CARD_WIDTH}px`,
              minWidth: `${CARD_WIDTH}px`,
            }}
            onClick={() => setCurrent(nextIdx)}
            inert="true"
          >
            <StoryCard {...nextCard} isPreview={true} />
          </div>
        )}
      </div>
      <div className="stories-carousel-buttons-container">
        <button
          className="stories-carousel-navigation-button"
          onClick={goPrev}
          disabled={showingAllStories}
          aria-label="Previous"
        >
          <FontAwesomeIcon icon={faArrowLeft} color="#58C8D8" size="lg" />
        </button>
        <button
          className="stories-carousel-navigation-button"
          onClick={goNext}
          disabled={showingAllStories}
          aria-label="Next"
        >
          <FontAwesomeIcon icon={faArrowRight} color="#58C8D8" size="lg" />
        </button>
      </div>
    </div>
  )
}
